import React, { useMemo } from 'react';
import Widget from './Widget';
import { CardHeader, CardTitle, CardContent } from '../ui/card';

const COLORS = [
  '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
];

/** Donut chart via SVG — center label is customizable */
const DonutChart = ({ data, size = 120, centerLabel, centerSub }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <div className="flex items-center justify-center" style={{ width: size, height: size }}><span className="text-xs text-textSecondary">No data</span></div>;
  const cx = size / 2, cy = size / 2, r = size * 0.38, stroke = size * 0.18;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {data.map((d, i) => {
        const pct = d.value / total;
        const dash = circ * pct;
        const gap = circ - dash;
        const el = (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={d.color || COLORS[i % COLORS.length]}
            strokeWidth={stroke}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
        offset += dash;
        return el;
      })}
      <text x={cx} y={cy - 6} textAnchor="middle" className="fill-textPrimary text-lg font-bold">
        {centerLabel ?? data.length}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" className="fill-textSecondary text-[10px]">
        {centerSub ?? 'items'}
      </text>
    </svg>
  );
};

/** Horizontal bar chart */
const HBarChart = ({ data, maxVal }) => {
  if (!data.length) return <p className="text-xs text-textSecondary">No data</p>;
  const mx = maxVal || Math.max(...data.map(d => Math.abs(d.value)), 1);
  return (
    <div className="space-y-1.5">
      {data.map((d, i) => {
        const pct = Math.min(Math.abs(d.value) / mx * 100, 100);
        const positive = d.value >= 0;
        return (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[11px] text-textSecondary w-16 truncate text-right" title={d.label}>{d.label}</span>
            <div className="flex-1 h-4 bg-backgroundBody rounded overflow-hidden relative">
              <div
                className={`h-full rounded ${positive ? 'bg-success' : 'bg-destructive'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className={`text-[11px] font-medium w-16 text-right ${positive ? 'text-success' : 'text-destructive'}`}>
              {d.value >= 0 ? '+' : ''}{d.value.toFixed(2)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/** Mini stat box */
const Stat = ({ label, value, color }) => (
  <div className="text-center">
    <p className="text-[10px] text-textSecondary leading-tight">{label}</p>
    <p className={`text-sm font-bold ${color || 'text-textPrimary'}`}>{value}</p>
  </div>
);

const fmt = (v, d = 2) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return Number(v).toFixed(d);
};

const pctColor = (v, threshold = 0) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '';
  return n > threshold ? 'text-success' : n < threshold ? 'text-destructive' : '';
};

const PortfolioWidget = ({ positions = [], balance, equity, hideTitle = false }) => {
  const hasPositions = positions.length > 0;

  // Aggregate by symbol: total volume and total P/L
  const bySymbol = useMemo(() => {
    const map = {};
    positions.forEach(p => {
      const sym = p.symbol || 'Unknown';
      if (!map[sym]) map[sym] = { volume: 0, pnl: 0, count: 0 };
      map[sym].volume += parseFloat(p.volume) || 0;
      map[sym].pnl += parseFloat(p.profit) || 0;
      map[sym].count += 1;
    });
    return Object.entries(map)
      .map(([sym, d]) => ({ symbol: sym, ...d }))
      .sort((a, b) => b.volume - a.volume);
  }, [positions]);

  // Allocation donut — by volume per symbol
  const allocationData = useMemo(() =>
    bySymbol.map((d, i) => ({
      label: d.symbol,
      value: d.volume,
      color: COLORS[i % COLORS.length],
    })),
  [bySymbol]);

  // Long vs Short exposure donut (by volume)
  const directionData = useMemo(() => {
    let longVol = 0, shortVol = 0;
    positions.forEach(p => {
      const vol = parseFloat(p.volume) || 0;
      const dir = (p.direction || '').toUpperCase();
      if (dir === 'BUY' || dir === 'LONG') longVol += vol;
      else shortVol += vol;
    });
    return [
      { label: 'Long', value: parseFloat(longVol.toFixed(4)), color: '#3b82f6' },
      { label: 'Short', value: parseFloat(shortVol.toFixed(4)), color: '#f97316' },
    ].filter(d => d.value > 0);
  }, [positions]);

  // P/L by symbol bar chart
  const pnlBars = useMemo(() =>
    bySymbol
      .map(d => ({ label: d.symbol, value: d.pnl }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8),
  [bySymbol]);

  // Summary stats
  const totalPnl = useMemo(() => positions.reduce((s, p) => s + (parseFloat(p.profit) || 0), 0), [positions]);
  const totalVolume = useMemo(() => positions.reduce((s, p) => s + (parseFloat(p.volume) || 0), 0), [positions]);

  return (
    <Widget className="h-full">
      {!hideTitle && (
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center justify-between">
            <span>Portfolio</span>
            <span className="text-[10px] font-normal text-textSecondary">
              {hasPositions
                ? `${positions.length} position${positions.length !== 1 ? 's' : ''}`
                : 'No positions'}
            </span>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className="pt-0 space-y-4 overflow-y-auto max-h-[500px]">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-2 bg-backgroundBody rounded-lg">
          <Stat label="Floating P/L" value={hasPositions ? `$${fmt(totalPnl)}` : '$0.00'} color={hasPositions ? pctColor(totalPnl) : ''} />
          <Stat label="Total Volume" value={hasPositions ? fmt(totalVolume, 2) : '0.00'} />
          <Stat label="Symbols" value={hasPositions ? bySymbol.length : 0} />
          <Stat
            label="P/L % Equity"
            value={hasPositions && equity ? `${fmt((totalPnl / equity) * 100)}%` : '0.00%'}
            color={hasPositions ? pctColor(totalPnl) : ''}
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Allocation by Symbol */}
          <div className="flex flex-col items-center">
            <p className="text-[11px] text-textSecondary font-medium mb-1">Allocation by Symbol</p>
            <DonutChart
              data={hasPositions ? allocationData : []}
              size={110}
              centerLabel={hasPositions ? bySymbol.length : 0}
              centerSub="symbols"
            />
            {hasPositions && (
              <div className="flex flex-wrap justify-center gap-2 mt-1">
                {allocationData.map((d, i) => (
                  <span key={i} className="flex items-center gap-1 text-[10px] text-textSecondary">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: d.color }} />
                    {d.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Long / Short Exposure */}
          <div className="flex flex-col items-center">
            <p className="text-[11px] text-textSecondary font-medium mb-1">Long / Short</p>
            <DonutChart
              data={hasPositions ? directionData : []}
              size={110}
              centerLabel={hasPositions ? fmt(totalVolume, 2) : '0.00'}
              centerSub="lots"
            />
            {hasPositions && (
              <div className="flex gap-3 mt-1">
                {directionData.map((d, i) => (
                  <span key={i} className="flex items-center gap-1 text-[10px] text-textSecondary">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: d.color }} />
                    {d.label} ({d.value})
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* P/L by Symbol */}
        <div>
          <p className="text-[11px] text-textSecondary font-medium mb-2">Floating P/L by Symbol</p>
          {hasPositions ? <HBarChart data={pnlBars} /> : <p className="text-xs text-textSecondary text-center py-2">No data</p>}
        </div>

        {/* Per-Symbol Breakdown Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-textSecondary border-b border-border">
                <th className="text-left py-1 font-medium">Symbol</th>
                <th className="text-right py-1 font-medium">Pos</th>
                <th className="text-right py-1 font-medium">Volume</th>
                <th className="text-right py-1 font-medium">P/L</th>
              </tr>
            </thead>
            <tbody>
              {hasPositions ? bySymbol.map((row, i) => (
                <tr key={i} className="border-b border-border/50">
                  <td className="py-1 text-textPrimary font-medium">{row.symbol}</td>
                  <td className="py-1 text-right text-textSecondary">{row.count}</td>
                  <td className="py-1 text-right text-textSecondary">{fmt(row.volume, 2)}</td>
                  <td className={`py-1 text-right font-medium ${pctColor(row.pnl)}`}>
                    {row.pnl >= 0 ? '+' : ''}{fmt(row.pnl)}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={4} className="py-3 text-center text-textSecondary">No open positions</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Widget>
  );
};

export default PortfolioWidget;
